/**
 * PropertyHeatMap - Heat map visualization for property price density
 * Shows price concentration across the map
 */
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { MapContainer, TileLayer, useMap } from 'react-leaflet';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Slider } from './ui/slider';
import { Label } from './ui/label';
import { 
  List, Maximize2, Minimize2, Flame, 
  DollarSign, TrendingUp, TrendingDown, Info
} from 'lucide-react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet.heat';

// Heat layer component
const HeatLayer = ({ points, options }) => {
  const map = useMap();
  const heatLayerRef = useRef(null);
  
  useEffect(() => {
    if (!map || points.length === 0) return;
    
    // Remove existing layer
    if (heatLayerRef.current) {
      map.removeLayer(heatLayerRef.current);
    }
    
    // Create heat layer
    heatLayerRef.current = L.heatLayer(points, {
      radius: options.radius || 25,
      blur: options.blur || 15,
      maxZoom: options.maxZoom || 17,
      max: options.max || 1.0,
      gradient: options.gradient || {
        0.0: '#3b82f6',
        0.25: '#22c55e',
        0.5: '#eab308',
        0.75: '#f97316',
        1.0: '#ef4444'
      }
    }).addTo(map);
    
    return () => {
      if (heatLayerRef.current) {
        map.removeLayer(heatLayerRef.current);
      }
    };
  }, [map, points, options]);
  
  return null;
};

// Map bounds controller
const MapBoundsController = ({ points }) => {
  const map = useMap();
  
  useEffect(() => {
    if (points && points.length > 0) {
      const bounds = L.latLngBounds(points.map(p => [p[0], p[1]]));
      map.fitBounds(bounds, { padding: [50, 50], maxZoom: 13 });
    }
  }, [points, map]);
  
  return null;
};

export default function PropertyHeatMap({ 
  listings = [], 
  country = 'LK',
  onListView,
  className = '' 
}) {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [heatMode, setHeatMode] = useState('price'); // 'price' or 'density'
  const [radius, setRadius] = useState(25);
  const [intensity, setIntensity] = useState(0.6);
  
  const currency = country === 'UK' ? '£' : 'Rs.';
  
  // Default center based on country
  const defaultCenter = country === 'UK' 
    ? [51.5074, -0.1278] 
    : [6.9271, 79.8612];
  
  // Calculate price statistics
  const priceStats = useMemo(() => {
    const prices = listings
      .filter(l => l.price && l.latitude && l.longitude)
      .map(l => l.price);
    
    if (prices.length === 0) return { min: 0, max: 0, avg: 0 };
    
    return {
      min: Math.min(...prices),
      max: Math.max(...prices),
      avg: prices.reduce((a, b) => a + b, 0) / prices.length
    };
  }, [listings]);
  
  // Generate heat points based on mode
  const heatPoints = useMemo(() => {
    const validListings = listings.filter(l => l.latitude && l.longitude);
    
    if (heatMode === 'density') {
      // Simple density - all points equal weight
      return validListings.map(l => [l.latitude, l.longitude, intensity]);
    } else {
      // Price-weighted heat map
      const { min, max } = priceStats;
      const range = max - min || 1;
      
      return validListings.map(l => {
        // Normalize price to 0-1 range
        const normalizedPrice = (l.price - min) / range;
        return [l.latitude, l.longitude, normalizedPrice * intensity];
      });
    }
  }, [listings, heatMode, intensity, priceStats]);
  
  // Heat map options
  const heatOptions = useMemo(() => ({
    radius,
    blur: 15,
    maxZoom: 17,
    max: 1.0,
    gradient: heatMode === 'price' 
      ? {
          0.0: '#22c55e',  // Green - low price
          0.3: '#84cc16',  // Lime
          0.5: '#eab308',  // Yellow - medium
          0.7: '#f97316',  // Orange
          1.0: '#ef4444'   // Red - high price
        }
      : {
          0.0: '#3b82f6',  // Blue - low density
          0.3: '#8b5cf6',  // Purple
          0.5: '#ec4899',  // Pink
          0.7: '#f97316',  // Orange
          1.0: '#ef4444'   // Red - high density
        }
  }), [radius, heatMode]);
  
  return (
    <div 
      className={`relative bg-gray-100 ${isFullscreen ? 'fixed inset-0 z-50' : 'h-[600px]'} ${className}`}
      data-testid="property-heat-map"
    >
      {/* Controls */}
      <div className="absolute top-4 left-4 z-[1000] flex flex-col gap-2">
        <Button 
          variant="secondary"
          size="sm"
          onClick={onListView}
          className="shadow-lg bg-white"
          data-testid="switch-to-list-btn"
        >
          <List className="w-4 h-4 mr-2" />
          List View
        </Button>
      </div>
      
      {/* Heat Map Controls Panel */}
      <div className="absolute top-4 left-1/2 -translate-x-1/2 z-[1000]">
        <div className="bg-white shadow-lg rounded-lg px-4 py-3 space-y-3 min-w-[280px]">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Flame className="w-5 h-5 text-orange-500" />
              <span className="font-semibold text-gray-800">Price Heat Map</span>
            </div>
            <Badge variant="secondary" className="text-xs">
              {listings.length} properties
            </Badge>
          </div>
          
          {/* Mode Toggle */}
          <div className="flex gap-2">
            <Button
              size="sm"
              variant={heatMode === 'price' ? 'default' : 'outline'}
              className={`flex-1 h-8 text-xs ${heatMode === 'price' ? 'bg-gradient-to-r from-green-500 to-red-500 text-white' : ''}`}
              onClick={() => setHeatMode('price')}
            >
              <DollarSign className="w-3 h-3 mr-1" />
              Price Heat
            </Button>
            <Button
              size="sm"
              variant={heatMode === 'density' ? 'default' : 'outline'}
              className={`flex-1 h-8 text-xs ${heatMode === 'density' ? 'bg-blue-600' : ''}`}
              onClick={() => setHeatMode('density')}
            >
              <Flame className="w-3 h-3 mr-1" />
              Density
            </Button>
          </div>
          
          {/* Radius Slider */}
          <div className="space-y-1">
            <div className="flex justify-between text-xs text-gray-600">
              <span>Radius</span>
              <span>{radius}px</span>
            </div>
            <Slider
              value={[radius]}
              onValueChange={([v]) => setRadius(v)}
              min={10}
              max={50}
              step={5}
              className="w-full"
            />
          </div>
          
          {/* Intensity Slider */}
          <div className="space-y-1">
            <div className="flex justify-between text-xs text-gray-600">
              <span>Intensity</span>
              <span>{Math.round(intensity * 100)}%</span>
            </div>
            <Slider
              value={[intensity * 100]}
              onValueChange={([v]) => setIntensity(v / 100)}
              min={20}
              max={100}
              step={10}
              className="w-full"
            />
          </div>
        </div>
      </div>
      
      {/* Fullscreen toggle */}
      <div className="absolute top-4 right-4 z-[1000]">
        <Button
          variant="secondary"
          size="icon"
          onClick={() => setIsFullscreen(!isFullscreen)}
          className="shadow-lg bg-white h-8 w-8"
        >
          {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
        </Button>
      </div>
      
      {/* Legend */}
      <div className="absolute bottom-4 right-4 z-[1000]">
        <div className="bg-white shadow-lg rounded-lg p-3 text-xs">
          <div className="font-medium mb-2 flex items-center gap-1">
            <Info className="w-3 h-3" />
            {heatMode === 'price' ? 'Price Legend' : 'Density Legend'}
          </div>
          {heatMode === 'price' ? (
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-green-500"></div>
                <span className="flex items-center gap-1">
                  <TrendingDown className="w-3 h-3" />
                  Low ({currency}{(priceStats.min / 1000).toFixed(0)}k)
                </span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-yellow-500"></div>
                <span>Medium ({currency}{(priceStats.avg / 1000).toFixed(0)}k)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-red-500"></div>
                <span className="flex items-center gap-1">
                  <TrendingUp className="w-3 h-3" />
                  High ({currency}{(priceStats.max / 1000).toFixed(0)}k)
                </span>
              </div>
            </div>
          ) : (
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-blue-500"></div>
                <span>Few properties</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-purple-500"></div>
                <span>Some properties</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-red-500"></div>
                <span>Many properties</span>
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Map Container */}
      <MapContainer
        center={defaultCenter}
        zoom={12}
        className="h-full w-full"
        zoomControl={false}
        style={{ zIndex: 1 }}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        
        <MapBoundsController points={heatPoints} />
        
        {heatPoints.length > 0 && (
          <HeatLayer points={heatPoints} options={heatOptions} />
        )}
      </MapContainer>
    </div>
  );
}
